import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  Loader2,
  Building2,
  User,
  Mail,
  Phone,
  FileText,
  MapPin,
  CalendarClock,
} from 'lucide-react';
import { getBrandById } from '../../services/adminbrands.api';
import { Brand } from '../../types/adminbrands-model';

interface BrandDetailModalProps {
  brandId: string | null;
  open: boolean;
  onClose: () => void;
}

const Row: React.FC<{ icon: React.ReactNode; label: string; value?: string | null }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-start gap-3 py-3">
    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">{label}</div>
      <div className="mt-0.5 truncate text-[13px] font-medium text-stone-800">
        {value && value.trim() !== '' ? value : <span className="text-stone-300">Not provided</span>}
      </div>
    </div>
  </div>
);

const BrandDetailModal: React.FC<BrandDetailModalProps> = ({ brandId, open, onClose }) => {
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBrand = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getBrandById(brandId);
      setBrand(data);
    } catch (err) {
      console.error('[BrandDetailModal] fetch error', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch brand details.');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  // Fetch when opened; reset state when closed so the next open starts fresh
  useEffect(() => {
    if (open && brandId) {
      fetchBrand();
    }
    if (!open) {
      setBrand(null);
      setError('');
      setLoading(false);
    }
  }, [open, brandId, fetchBrand]);

  // Lock background scroll + Escape-to-close while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);


  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-stone-900/40 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-stone-200 bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-stone-100 bg-white/95 px-6 py-5 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-[16px] font-bold text-white shadow-md shadow-amber-200">
              {brand ? brand.companyName.charAt(0).toUpperCase() : <Building2 size={18} />}
            </div>
            <div>
              <div className="font-serif text-[17px] font-bold text-stone-800">
                {brand ? brand.companyName : 'Brand Details'}
              </div>
              {brand && <div className="text-[11px] text-stone-400">Brand #{brand.sno}</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-stone-400 transition hover:bg-stone-100 hover:text-stone-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 size={26} className="animate-spin text-amber-600" />
              <p className="mt-3 text-[13px] text-stone-400">Loading brand details...</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-[13px] text-rose-600">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 rounded-lg border border-stone-200 px-4 py-2 text-[12px] font-medium text-stone-600 hover:bg-stone-50"
              >
                Close
              </button>
            </div>
          )}

          {!loading && !error && brand && (
            <div className="divide-y divide-stone-100">
              <Row icon={<User size={15} />} label="Contact Person" value={brand.contactPerson} />
              <Row icon={<Mail size={15} />} label="Email" value={brand.email} />
              <Row icon={<Phone size={15} />} label="Mobile" value={brand.mobile} />
              <Row icon={<FileText size={15} />} label="GST Number" value={brand.gst} />
              <Row icon={<MapPin size={15} />} label="Billing Address" value={brand.billingAddress} />
              <Row
                icon={<CalendarClock size={15} />}
                label="Registered On"
                value={`${brand.edate} at ${brand.ctime}`}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && brand && (
          <div className="flex justify-end gap-2 border-t border-stone-100 bg-stone-50/60 px-6 py-4">
            <button
              onClick={onClose}
              className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-[12px] font-medium text-stone-600 transition hover:bg-stone-100"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandDetailModal;